import React, { useState, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { Tag } from "antd";
import Icon from "@ant-design/icons";
import { TrashRedSvg } from "../../assets/icons";

const TrashIcon = (props) => <Icon component={TrashRedSvg} {...props} />;
import { useMarketView } from "../../hooks/UserManagementPage/useMarketView";
import { useUserTableColumns } from "../../hooks/UserManagementPage/useUserTableColumns";
import { useWorkspaceMarkets } from "../../hooks/UserManagementPage/useWorkspaceMarkets";
import { getAllMarketCodesFromRegionData } from "../../api/userManagementApi";
import { CustomPagination } from "./CustomPagination";
import {
  MarketViewContainer,
  MarketCollapseHeader,
  MarketCollapseContent,
  MarketTable,
  MarketPaginationWrapper,
  ExpandAllControls
} from "../../styles/UserManagement.styled";

// Separate component for each market's table to allow proper Hook usage
const MarketTableComponent = ({
  marketUsers,
  allUsers,
  editingRows,
  openDropdowns,
  stagingData,
  getRoleTagOption,
  commonButtonStyles,
  handleMarketDropdownOpenChange,
  handleRegionExpansion,
  handleRegionSelection,
  handleMarketSelection,
  isRegionFullySelected,
  isMarketSelected,
  handleRoleDropdownOpenChange,
  handleProductDropdownOpenChange,
  handleStageChange,
  handleSaveUser,
  startEditUser,
  stopEditUser,
  handleEditUser,
  handleDeleteUser,
  handleResendInvitation,
  handleAddModule,
  sortedRegionMarketData,
  currentUserEmail,
  isSaving,
}) => {
  // Function to calculate row spans for this market's users (same logic as main table)
  const calculateRowSpansForMarket = (users) => {
    const rowSpans = {};

    if (!users || users.length === 0) {
      return rowSpans;
    }

    // Group data by user and role for proper calculation (same as main table)
    const userGroups = {};
    users.forEach((row) => {
      if (!userGroups[row.userId]) {
        userGroups[row.userId] = {};
      }
      if (!userGroups[row.userId][row.roleIndex]) {
        userGroups[row.userId][row.roleIndex] = [];
      }
      userGroups[row.userId][row.roleIndex].push(row);
    });

    users.forEach((row) => {
      const { userId, roleIndex } = row;

      // Calculate user column rowSpan - only for the first row of the user in THIS market
      const userRowsInMarket = users.filter(r => r.userId === userId);
      const isFirstUserRow = userRowsInMarket[0]?.key === row.key;
      if (isFirstUserRow) {
        rowSpans[`${row.key}-name`] = userRowsInMarket.length;
      }

      // Calculate role column rowSpan - only for the first product of each role in THIS market
      const roleRowsInMarket = userGroups[userId][roleIndex];
      const isFirstRoleRow = roleRowsInMarket[0]?.key === row.key;
      if (isFirstRoleRow) {
        rowSpans[`${row.key}-role`] = roleRowsInMarket.length;
      }

      // Product, Market, Action columns always have rowSpan 1
      rowSpans[`${row.key}-product`] = 1;
      rowSpans[`${row.key}-market`] = 1;
      rowSpans[`${row.key}-action`] = 1;
    });

    return rowSpans;
  };

  // Use the original useUserTableColumns with the market-specific data and rowSpan function
  const { columns } = useUserTableColumns({
    editingRows,
    openDropdowns,
    stagingData,
    getRoleTagOption,
    commonButtonStyles,
    handleMarketDropdownOpenChange,
    handleRegionExpansion,
    handleRegionSelection,
    handleMarketSelection,
    isRegionFullySelected,
    isMarketSelected,
    handleRoleDropdownOpenChange,
    handleProductDropdownOpenChange,
    handleStageChange,
    handleSaveUser,
    startEditUser,
    stopEditUser,
    handleEditUser,
    handleDeleteUser,
    handleResendInvitation,
    handleAddModule,
    users: marketUsers,
    allUsers: allUsers,
    calculateRowSpans: calculateRowSpansForMarket,
    regionMarketData: sortedRegionMarketData,
    currentUserEmail,
    isSaving,
  });

  return { columns };
};

export const MarketView = ({
  users,
  searchTerm,
  editingRows,
  openDropdowns,
  stagingData,
  getRoleTagOption,
  commonButtonStyles,
  handleMarketDropdownOpenChange,
  handleRegionExpansion,
  handleRegionSelection,
  handleMarketSelection,
  isRegionFullySelected,
  isMarketSelected,
  handleRoleDropdownOpenChange,
  handleProductDropdownOpenChange,
  handleStageChange,
  handleSaveUser,
  startEditUser,
  stopEditUser,
  handleEditUser,
  handleDeleteUser,
  handleResendInvitation,
  handleAddModule,
  currentUserEmail,
  isSaving,
  onBulkDelete,
  setSelectedRows,
}) => {
  // Get sorted region-market data from useWorkspaceMarkets hook (at top level)
  const { currentClientRegionMarketData, globalRegionMarketData } = useWorkspaceMarkets();

  // Sort region-market data alphabetically (regions and markets)
  const sortedRegionMarketData = useMemo(() => {
    // Use currentClientRegionMarketData if available, otherwise fall back to globalRegionMarketData
    const regionMarketDataToSort =
      (currentClientRegionMarketData && Object.keys(currentClientRegionMarketData).length > 0)
        ? currentClientRegionMarketData
        : globalRegionMarketData;

    if (!regionMarketDataToSort || Object.keys(regionMarketDataToSort).length === 0) {
      return regionMarketDataToSort;
    }

    // Sort regions alphabetically
    const sortedRegions = Object.entries(regionMarketDataToSort)
      .sort(([regionA], [regionB]) => {
        const titleA = (regionA || '').toLowerCase();
        const titleB = (regionB || '').toLowerCase();
        return titleA.localeCompare(titleB);
      })
      .map(([regionName, regionData]) => {
        // Sort markets alphabetically within each region
        const sortedMarkets = [...(regionData.markets || [])].sort((a, b) => {
          const marketA = (a || '').toLowerCase();
          const marketB = (b || '').toLowerCase();
          return marketA.localeCompare(marketB);
        });

        return [
          regionName,
          {
            ...regionData,
            markets: sortedMarkets,
            // Also sort scopes keys if needed
            scopes: sortedMarkets.reduce((acc, market) => {
              acc[market] = regionData.scopes?.[market] || "Deepdive; Execution view; Market Overview";
              return acc;
            }, {}),
          },
        ];
      });

    // Convert back to object format
    return Object.fromEntries(sortedRegions);
  }, [currentClientRegionMarketData, globalRegionMarketData]);

  // All available market codes — used to expand "All Markets" rows (super admins)
  const allAvailableCodes = useMemo(
    () => getAllMarketCodesFromRegionData(sortedRegionMarketData),
    [sortedRegionMarketData]
  );

  const {
    marketGroups,
    expandedMarkets,
    handleMarketToggle,
    expandAllMarkets,
    loading: marketViewLoading,
    contextHolder: marketViewContextHolder,
  } = useMarketView(users, searchTerm, allAvailableCodes);

  // State for row selection per market
  const [marketSelectedRows, setMarketSelectedRows] = useState({});

  const getMarketRowSelection = (marketName, paginatedMarketUsers) => {
    const selectedKeys = marketSelectedRows[marketName] || [];
    return {
      selectedRowKeys: selectedKeys,
      onChange: (newSelectedKeys) => {
        // Store exactly what Ant Design gives us — rowClassName handles
        // highlighting all rows of a selected user without expansion,
        // so deselection works correctly (no re-expansion loop).
        setMarketSelectedRows((prev) => ({
          ...prev,
          [marketName]: newSelectedKeys,
        }));
      },
      renderCell: (checked, record, _index, originNode) => {
        const userRows = paginatedMarketUsers.filter(
          (u) => u.userId === record.userId
        );
        const isFirstRow = userRows[0]?.key === record.key;
        // Current user: empty cell with correct rowSpan, no checkbox
        if (currentUserEmail && record.email === currentUserEmail) {
          return {
            props: { rowSpan: isFirstRow ? userRows.length : 0 },
            children: null,
          };
        }
        return {
          props: { rowSpan: isFirstRow ? userRows.length : 0 },
          children: isFirstRow ? originNode : null,
        };
      },
    };
  };

  // State for pagination per market
  const [marketPagination, setMarketPagination] = useState({});

  // Handle pagination for specific market
  const handleMarketPaginationChange = (marketName, page, pageSize) => {
    setMarketPagination(prev => ({
      ...prev,
      [marketName]: { page, pageSize }
    }));
  };

  // Count unique selected users across all markets
  const totalSelectedCount = useMemo(() => {
    const userIds = new Set();
    Object.entries(marketSelectedRows).forEach(([marketName, selectedKeys]) => {
      const group = marketGroups.find(([name]) => name === marketName);
      if (!group) return;
      const [, marketUsers] = group;
      selectedKeys.forEach((key) => {
        const user = marketUsers.find((u) => u.key === key);
        if (user) userIds.add(user.userId);
      });
    });
    return userIds.size;
  }, [marketSelectedRows, marketGroups]);

  // Collect all selected keys and sync to shared state, then open bulk delete modal
  const handleMarketBulkDelete = () => {
    // Collect the unique userIds selected in Market View
    const selectedUserIds = new Set();
    Object.entries(marketSelectedRows).forEach(([marketName, selectedKeys]) => {
      const group = marketGroups.find(([name]) => name === marketName);
      if (!group) return;
      const [, marketUsers] = group;
      selectedKeys.forEach((key) => {
        const user = marketUsers.find((u) => u.key === key);
        if (user) selectedUserIds.add(user.userId);
      });
    });

    // Resolve to User List row keys so confirmBulkDelete can find the users
    const matchingKeys = users
      .filter((u) => selectedUserIds.has(u.userId))
      .map((u) => u.key);

    setSelectedRows(matchingKeys);
    onBulkDelete();
  };

  // Get paginated data for a market.
  //
  // `marketUsers` is a flat list with one row per user-module, and the Name/Role
  // cells merge across a user's rows with rowSpan. Slicing the flat list by
  // pageSize splits a user mid-page (so "10 rows" can show only ~2 users) and
  // orphans rowSpan'd rows. Paginate the ordered unique userIds instead, then
  // keep every row of the page's users — mirroring useUserFiltering.js. pageSize
  // counts users.
  const getPaginatedMarketData = (marketUsers, marketName) => {
    const pagination = marketPagination[marketName] || { page: 1, pageSize: 10 };
    const orderedUserIds = [];
    const seen = new Set();
    for (const row of marketUsers) {
      if (!seen.has(row.userId)) {
        seen.add(row.userId);
        orderedUserIds.push(row.userId);
      }
    }
    const startIndex = (pagination.page - 1) * pagination.pageSize;
    const pageUserIds = new Set(
      orderedUserIds.slice(startIndex, startIndex + pagination.pageSize)
    );
    return {
      data: marketUsers.filter((row) => pageUserIds.has(row.userId)),
      total: orderedUserIds.length,
      current: pagination.page,
      pageSize: pagination.pageSize
    };
  };

  // Show loading state
  if (marketViewLoading) {
    return (
      <MarketViewContainer>
        {marketViewContextHolder}
        <div className="text-center py-8 text-gray-500">
          Loading market groups...
        </div>
      </MarketViewContainer>
    );
  }

  // Show empty state
  if (marketGroups.length === 0) {
    return (
      <MarketViewContainer>
        {marketViewContextHolder}
        <div className="text-center py-8" style={{ color: "#a1a1aa" }}>
          {searchTerm ? `No users match "${searchTerm}"` : "No users found"}
        </div>
      </MarketViewContainer>
    );
  }

  return (
    <MarketViewContainer>
      {marketViewContextHolder}
      {/* Header: title + selected count + delete button + expand all */}
      <ExpandAllControls className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <span className="text-[#000000d9] font-[Satoshi] font-bold text-[1.5rem]">All Markets</span>
          {totalSelectedCount > 0 && (
            <>
              <span style={{ fontFamily: "Inter", fontSize: "14px", color: "#6B7280", fontWeight: 500 }}>
                {totalSelectedCount} user{totalSelectedCount > 1 ? "s" : ""} selected
              </span>
              <button
                onClick={handleMarketBulkDelete}
                className="flex items-center bg-[#fef6f6] gap-2 px-4 py-2 border-[#df0101] border-[2px] text-[#df0101] text-xs font-medium rounded-full transition-colors cursor-pointer"
              >
                <TrashIcon className="w-4 h-4" />
                Delete
              </button>
            </>
          )}
        </div>
        <button
          className="expand-control-btn"
          onClick={expandAllMarkets}
        >
          Expand All
        </button>
      </ExpandAllControls>

      {/* Market Groups */}
      {marketGroups.map(([marketName, marketUsers, roleStats]) => {
        const isExpanded = expandedMarkets.has(marketName);
        const paginatedData = getPaginatedMarketData(marketUsers, marketName);

        // Get columns for this market
        const { columns } = MarketTableComponent({
          marketUsers: paginatedData.data,
          allUsers: marketUsers,
          editingRows,
          openDropdowns,
          stagingData,
          getRoleTagOption,
          commonButtonStyles,
          handleMarketDropdownOpenChange,
          handleRegionExpansion,
          handleRegionSelection,
          handleMarketSelection,
          isRegionFullySelected,
          isMarketSelected,
          handleRoleDropdownOpenChange,
          handleProductDropdownOpenChange,
          handleStageChange,
          handleSaveUser,
          startEditUser,
          stopEditUser,
          handleEditUser,
          handleDeleteUser,
          handleResendInvitation,
          handleAddModule,
          sortedRegionMarketData,
          currentUserEmail,
          isSaving,
        });

        const marketRowSelection = getMarketRowSelection(marketName, paginatedData.data);

        return (
          <div key={marketName}>
            {/* Market Header */}
            <MarketCollapseHeader
              isExpanded={isExpanded}
              onClick={() => handleMarketToggle(marketName)}
            >
              <div className="flex items-center">
                <span className="market-title">{marketName}</span>
                {/* Unique users, not user-module rows (paginatedData.total dedups by userId). */}
                <span className="user-count">({paginatedData.total})</span>
                {/* Role Statistics */}
                <div className="flex items-center gap-2 ml-4">
                  {roleStats['super_admin'] > 0 && (
                    <Tag
                      style={{
                        ...getRoleTagOption('super_admin').label.props.style,
                      }}
                    >
                      {roleStats['super_admin']} Super Admin{roleStats['super_admin'] > 1 ? 's' : ''}
                    </Tag>
                  )}
                  {roleStats['admin'] > 0 && (
                    <Tag
                      style={{
                        ...getRoleTagOption('admin').label.props.style,
                      }}
                    >
                      {roleStats['admin']} Admin{roleStats['admin'] > 1 ? 's' : ''}
                    </Tag>
                  )}
                  {roleStats['normal_user'] > 0 && (
                    <Tag
                      style={{
                        ...getRoleTagOption('normal_user').label.props.style,
                      }}
                    >
                      {roleStats['normal_user']} Normal User{roleStats['normal_user'] > 1 ? 's' : ''}
                    </Tag>
                  )}
                </div>
              </div>
              <ChevronDown className="expand-icon w-5 h-5" />
            </MarketCollapseHeader>

            {/* Market Content */}
            {isExpanded && (
              <MarketCollapseContent>
                <MarketTable
                  rowSelection={marketRowSelection}
                  columns={columns}
                  dataSource={paginatedData.data}
                  pagination={false}
                  locale={{ emptyText: 'No users found' }}
                />

                {/* Pagination */}
                {marketUsers.length > 10 && (
                  <MarketPaginationWrapper>
                    <CustomPagination
                      total={paginatedData.total}
                      current={paginatedData.current}
                      pageSize={paginatedData.pageSize}
                      onChange={(page, pageSize) =>
                        handleMarketPaginationChange(marketName, page, pageSize)
                      }
                      onShowSizeChange={(current, size) =>
                        handleMarketPaginationChange(marketName, 1, size)
                      }
                    />
                  </MarketPaginationWrapper>
                )}
              </MarketCollapseContent>
            )}
          </div>
        );
      })}
    </MarketViewContainer>
  );
};
